import {
	createIframe,
	destroyIframe,
	hideIframe,
	type IframeState,
	isMobileViewport,
	resizeIframe,
	sendMessageToIframe,
	showIframe,
} from "./iframe";
import {
	createLauncher,
	destroyLauncher,
	type LauncherState,
	updateLauncherState,
} from "./launcher";
import type {
	WidgetConfig,
	WidgetInstance,
	WidgetToParentMessage,
} from "./types";

// Export types
export type { WidgetConfig, WidgetInstance } from "./types";

/**
 * Internal widget state
 */
interface WidgetState {
	config: WidgetConfig;
	launcher: LauncherState | null;
	iframe: IframeState | null;
	isOpen: boolean;
	isAuthenticating: boolean;
	messageHandler: ((event: MessageEvent) => void) | null;
}

let widgetState: WidgetState | null = null;
let clickOutsideHandler: ((e: MouseEvent) => void) | null = null;

/**
 * Setup click outside handler to close widget
 */
function setupClickOutside(): void {
	if (!widgetState || clickOutsideHandler) return;

	clickOutsideHandler = (e: MouseEvent) => {
		if (!widgetState?.isOpen) return;

		const target = e.target as Node;
		const launcher = widgetState.launcher?.button;
		const container = widgetState.iframe?.container;

		// Don't close if clicking launcher or widget container
		if (launcher?.contains(target) || container?.contains(target)) {
			return;
		}

		close();
	};

	document.addEventListener("mousedown", clickOutsideHandler);
}

/**
 * Remove click outside handler
 */
function removeClickOutside(): void {
	if (clickOutsideHandler) {
		document.removeEventListener("mousedown", clickOutsideHandler);
		clickOutsideHandler = null;
	}
}

/**
 * Check if origin is a valid fdback.io domain, custom baseUrl, or development environment
 */
function isValidOrigin(origin: string, baseUrl?: string): boolean {
	// If custom baseUrl is configured, accept it
	if (baseUrl) {
		try {
			const baseOrigin = new URL(baseUrl).origin;
			if (origin === baseOrigin) {
				return true;
			}
		} catch {
			// Invalid baseUrl, continue with default checks
		}
	}

	// Production origins
	if (
		origin === "https://fdback.io" ||
		origin === "https://app.fdback.io" ||
		origin.endsWith(".fdback.io")
	) {
		return true;
	}

	// Development environments
	if (
		origin.startsWith("http://localhost:") ||
		origin.startsWith("http://127.0.0.1:")
	) {
		return true;
	}

	// Local development domains (.test, .local)
	try {
		const url = new URL(origin);
		if (url.hostname.endsWith(".test") || url.hostname.endsWith(".local")) {
			return true;
		}
	} catch {
		// Invalid URL, reject
	}

	return false;
}

/**
 * Handle messages from the widget iframe
 */
function handleMessage(event: MessageEvent): void {
	if (!widgetState) return;

	// Validate origin - only accept messages from fdback.io or configured baseUrl
	if (!isValidOrigin(event.origin, widgetState.config.baseUrl)) return;

	const message = event.data as WidgetToParentMessage;
	if (!message?.type?.startsWith("fdback:")) return;

	switch (message.type) {
		case "fdback:ready":
			if (widgetState.iframe) {
				widgetState.iframe.isReady = true;
				// Send init config to iframe (without callbacks - they can't be cloned)
				const {
					onOpen: _onOpen,
					onClose: _onClose,
					onFeedbackSubmitted: _onFeedbackSubmitted,
					onTabChange: _onTabChange,
					onAuthenticated: _onAuthenticated,
					...serializableConfig
				} = widgetState.config;
				sendMessageToIframe(widgetState.iframe, {
					type: "fdback:init",
					config: serializableConfig,
				});

				// If signEndpoint is configured, authenticate user
				if (widgetState.config.signEndpoint) {
					authenticateUser(widgetState.config.signEndpoint);
				}
			}
			break;

		case "fdback:tabChanged":
			widgetState.config.onTabChange?.(message.tab);
			break;

		case "fdback:feedbackSubmitted":
			widgetState.config.onFeedbackSubmitted?.(message.feedback);
			break;

		case "fdback:authenticated":
			widgetState.config.onAuthenticated?.(message.user);
			break;

		case "fdback:authError":
			console.error("[fdback] Authentication error:", message.error);
			break;

		case "fdback:resize":
			if (widgetState.iframe) {
				resizeIframe(widgetState.iframe, message.height);
			}
			break;

		case "fdback:requestClose":
			// Widget requested close (e.g., mobile close button)
			close();
			break;
	}
}

/**
 * Authenticate user via sign endpoint
 */
async function authenticateUser(signEndpoint: string): Promise<void> {
	if (!widgetState?.iframe) return;

	// Prevent multiple authentication attempts
	if (widgetState.isAuthenticating) return;
	widgetState.isAuthenticating = true;

	try {
		// Fetch signed data from the customer's sign endpoint
		// The endpoint gets user data from their session
		const response = await fetch(signEndpoint, {
			method: "POST",
			credentials: "include", // Send cookies for session
		});

		if (!response.ok) {
			// User not logged in or sign failed - continue as anonymous
			console.debug(
				"[fdback] Sign endpoint returned non-OK status, continuing as anonymous",
			);
			return;
		}

		const signedData = await response.json();

		// Validate signed data structure
		if (!signedData.headers || !signedData.body || !signedData.expiresAt) {
			console.error("[fdback] Invalid signed data structure");
			return;
		}

		// Send signed data to iframe for authentication
		sendMessageToIframe(widgetState.iframe, {
			type: "fdback:authenticate",
			signedData,
		});
	} catch (error) {
		// Network error or other issue - continue as anonymous
		console.debug(
			"[fdback] Sign endpoint error, continuing as anonymous:",
			error,
		);
	}
}

/**
 * Initialize the widget
 */
function init(config: WidgetConfig): WidgetInstance {
	// Destroy existing widget if any
	if (widgetState) {
		destroy();
	}

	// Create state
	widgetState = {
		config,
		launcher: null,
		iframe: null,
		isOpen: false,
		isAuthenticating: false,
		messageHandler: handleMessage,
	};

	// Create launcher button
	widgetState.launcher = createLauncher(config, toggle);

	// Create iframe (hidden)
	widgetState.iframe = createIframe(config);

	// Set up viewport change handler to update launcher visibility
	widgetState.iframe.onViewportChange = (isMobile: boolean) => {
		if (widgetState?.launcher && widgetState.isOpen) {
			updateLauncherState(widgetState.launcher, widgetState.isOpen, isMobile);
		}
	};

	// Listen for messages from iframe
	window.addEventListener("message", handleMessage);

	return {
		open,
		close,
		toggle,
		setTab,
		setUser,
		destroy,
		get isOpen() {
			return widgetState?.isOpen ?? false;
		},
	};
}

/**
 * Open the widget
 */
function open(): void {
	if (!widgetState || widgetState.isOpen) return;

	const isMobile = isMobileViewport();
	widgetState.isOpen = true;

	if (widgetState.launcher) {
		updateLauncherState(widgetState.launcher, true, isMobile);
	}

	if (widgetState.iframe) {
		showIframe(widgetState.iframe);
		sendMessageToIframe(widgetState.iframe, { type: "fdback:open" });
	}

	widgetState.config.onOpen?.();

	// Setup click outside to close (only on desktop)
	if (!isMobile) {
		setupClickOutside();
	}
}

/**
 * Close the widget
 */
function close(): void {
	if (!widgetState || !widgetState.isOpen) return;

	const isMobile = isMobileViewport();

	// Remove click outside handler
	removeClickOutside();

	widgetState.isOpen = false;

	if (widgetState.launcher) {
		updateLauncherState(widgetState.launcher, false, isMobile);
	}

	if (widgetState.iframe) {
		hideIframe(widgetState.iframe);
		sendMessageToIframe(widgetState.iframe, { type: "fdback:close" });
	}

	widgetState.config.onClose?.();
}

/**
 * Toggle widget open/closed
 */
function toggle(): void {
	if (widgetState?.isOpen) {
		close();
	} else {
		open();
	}
}

/**
 * Change to a specific tab
 */
function setTab(tab: "feedback" | "roadmap" | "changelog"): void {
	if (!widgetState?.iframe) return;

	sendMessageToIframe(widgetState.iframe, {
		type: "fdback:setTab",
		tab,
	});
}

/**
 * Update user data
 */
function setUser(user: { email: string; name?: string }): void {
	if (!widgetState?.iframe) return;

	sendMessageToIframe(widgetState.iframe, {
		type: "fdback:setUser",
		user,
	});
}

/**
 * Destroy the widget and clean up
 */
function destroy(): void {
	if (!widgetState) return;

	// Remove click outside handler
	removeClickOutside();

	// Remove message listener
	window.removeEventListener("message", handleMessage);

	// Destroy UI elements
	if (widgetState.launcher) {
		destroyLauncher(widgetState.launcher);
	}

	if (widgetState.iframe) {
		destroyIframe(widgetState.iframe);
	}

	widgetState = null;
}

/**
 * Widget module
 */
export const widget = {
	init,
	open,
	close,
	toggle,
	setTab,
	setUser,
	destroy,
};
